import React, { useCallback, useEffect, useRef, useState } from 'react';

import LookupPicker from '../additional_payments/lookup_picker';
import { recalc_payload } from '../additional_payments/upload/requests';
import { fetch_additional_teachers, post_recalc } from '../api';
import { error_message } from '../format';
import {
  MAX_SELECTED,
  add_selected,
  cancel_queued,
  mark_error,
  mark_result,
  mark_running,
  next_queued,
  remove_selected,
  result_row_view,
  rows_active,
  rows_summary,
  selected_block_reason,
  selected_button_text,
  shown_teacher_name,
  start_rows
} from './logic';

// "Name — uuid" with only the uuid in monospace.
export const TeacherRef = ({ teacher_uuid, teacher_name }) => {
  const name = shown_teacher_name({ teacher_uuid, teacher_name });
  return (
    <span className="tp-rc-teacher">
      {name ? `${name} — ` : ''}
      <span className="tp-rc-uuid">{teacher_uuid}</span>
    </span>
  );
};

const search_teachers = (q) =>
  fetch_additional_teachers(q).then((d) => (Array.isArray(d.rows) ? d.rows : []));

// Recalculate a picked list of teachers one at a time (POST /recalc each, never in parallel).
// on_busy(bool) tells the tab a run is active, so the month-wide start stays off meanwhile.
const SelectedTeachers = ({ pay_month, month_label, month_running, on_busy }) => {
  const [chips, setChips] = useState([]);
  const [note, setNote] = useState('');
  const [rows, setRows] = useState([]);
  const [stopping, setStopping] = useState(false);
  const rows_ref = useRef([]);
  const stop_ref = useRef(false);
  // a month change or unmount bumps this; the loop for the old month stops at its next step.
  const gen = useRef(0);

  const put_rows = (next) => {
    rows_ref.current = next;
    setRows(next);
  };

  useEffect(() => {
    gen.current += 1;
    put_rows([]);
    setStopping(false);
    return () => {
      gen.current += 1;
    };
  }, [pay_month]);

  const busy = rows_active(rows);
  useEffect(() => {
    if (on_busy) on_busy(busy);
  }, [busy, on_busy]);

  const step = useCallback(
    (my) => {
      if (my !== gen.current) return;
      if (stop_ref.current) put_rows(cancel_queued(rows_ref.current));
      const i = next_queued(rows_ref.current);
      if (i === -1) {
        setStopping(false);
        return;
      }
      put_rows(mark_running(rows_ref.current, i));
      post_recalc(recalc_payload(rows_ref.current[i].teacher_uuid, pay_month))
        .then((data) => {
          if (my === gen.current) put_rows(mark_result(rows_ref.current, i, data));
        })
        .catch((e) => {
          if (my === gen.current) {
            put_rows(
              mark_error(rows_ref.current, i, error_message(e, 'The recalculation failed.'))
            );
          }
        })
        .finally(() => step(my));
    },
    [pay_month]
  );

  const start = () => {
    stop_ref.current = false;
    setStopping(false);
    put_rows(start_rows(chips));
    step(gen.current);
  };

  const stop = () => {
    stop_ref.current = true;
    setStopping(true);
  };

  const pick = (t) => {
    const out = add_selected(chips, t);
    setChips(out.chips);
    setNote(out.note);
  };

  const block = selected_block_reason({ chips, month_running, busy });
  const summary = !busy && rows.length ? rows_summary(rows) : '';

  return (
    <section className="tp-rc-sel" aria-label="Recalculate selected teachers">
      <h3 className="tp-rc-sel-title">Recalculate selected teachers</h3>
      <LookupPicker
        id="tp-rc-sel-teacher"
        name="teacher"
        label="Add a teacher"
        placeholder="Search by name, slug or email"
        min_chars={2}
        value_text=""
        disabled={busy}
        search={search_teachers}
        option_key={(t) => t.teacher_uuid}
        render_option={(t) => (
          <span>
            <b>{t.name}</b>
            {t.email ? ` · ${t.email}` : ''} · <span className="tp-rc-uuid">{t.teacher_uuid}</span>
          </span>
        )}
        on_pick={pick}
        on_clear={() => {}}
      />
      {note ? <div className="tp-hint">{note}</div> : null}
      {chips.length ? (
        <ul className="tp-rc-chips" aria-label="Selected teachers">
          {chips.map((c) => (
            <li key={c.teacher_uuid} className="tp-rc-chip">
              <TeacherRef teacher_uuid={c.teacher_uuid} teacher_name={c.teacher_name} />
              <button
                type="button"
                className="tp-rc-chip-x"
                aria-label={`Remove ${c.teacher_name || c.teacher_uuid}`}
                disabled={busy}
                onClick={() => {
                  setChips(remove_selected(chips, c.teacher_uuid));
                  setNote('');
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="tp-hint">{`${chips.length} of at most ${MAX_SELECTED} teachers.`}</div>
      <div className="tp-rc-actions">
        {busy ? (
          <button type="button" className="tp-btn tp-btn-out" disabled={stopping} onClick={stop}>
            {stopping ? 'Stopping after this teacher…' : 'Stop'}
          </button>
        ) : (
          <button
            type="button"
            className="tp-btn"
            disabled={Boolean(block) || !pay_month}
            onClick={start}
          >
            {selected_button_text(chips.length, month_label)}
          </button>
        )}
      </div>
      {block && chips.length ? <div className="tp-banner tp-banner-info">{block}</div> : null}
      {rows.length ? (
        <ul className="tp-rc-results" data-testid="tp-rc-results">
          {rows.map((r) => {
            const v = result_row_view(r);
            return (
              <li key={v.key} className={`tp-rc-res tp-rc-res-${v.state}`}>
                <TeacherRef teacher_uuid={v.teacher_uuid} teacher_name={v.teacher_name} />
                <span className="tp-rc-res-state">{v.state_text}</span>
                {v.detail ? <span>{v.detail}</span> : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {summary ? <div className="tp-hint">{summary}</div> : null}
    </section>
  );
};

export default SelectedTeachers;
